/**
 * OnboardingStep5.tsx - Motivations & Semester Goal
 * Motivation step of the onboarding flow.
 * Collects user motivations (multi-select) and semester goal (dropdown).
 * Goal options are translated dynamically from the backend.
 */
import React, { useMemo } from "react";
import { View } from "react-native";
import { useTranslation } from "react-i18next";
import OnboardingStepHeader from "./common/OnboardingStepHeader";
import OnboardingFormSection from "./common/OnboardingFormSection";
import CustomDropdown from "../../common/general/CustomDropDown/CustomDropdown";
import ChipSelector, { ChipOption } from "../../common/form/ChipSelect/ChipSelector";
import { spacingStyles } from "../../../styles/design-system";

export interface OnboardingStep5Props {
  selectedMotivations: string[];
  onMotivationsChange: (motivations: string[]) => void;
  selectedGoal: string;
  onGoalChange: (goal: string) => void;
  goalOptions: { key: string; value: string }[];
}

function OnboardingStep5({
  selectedMotivations,
  onMotivationsChange,
  selectedGoal,
  onGoalChange,
  goalOptions,
}: OnboardingStep5Props) {
  const { t } = useTranslation();
  const motivationOptions: ChipOption[] = useMemo(
    () => [
      { key: "better_grades", value: t("text.companion_onboarding_motivation_grades") },
      { key: "deep_understanding", value: t("text.companion_onboarding_motivation_understanding") },
      { key: "more_confidence", value: t("text.companion_onboarding_motivation_confidence") },
      { key: "learning_faster", value: t("text.companion_onboarding_motivation_learning_faster") },
    ],
    [t]
  );

  return (
    <View style={spacingStyles.mNone}>
      <OnboardingStepHeader
        titleKey="companion_onboarding_step5_title"
        subtitleKey="companion_onboarding_step5_subtitle"
      />

      <OnboardingFormSection labelKey="companion_onboarding_step5_motivations_label" isUppercase>
        <ChipSelector
          options={motivationOptions}
          selectedKeys={selectedMotivations}
          onSelectionChange={onMotivationsChange}
          multiSelect
        />
      </OnboardingFormSection>

      <OnboardingFormSection labelKey="companion_onboarding_step5_goal_label" isUppercase>
        <CustomDropdown
          options={goalOptions.map((opt) => ({ label: opt.value, value: opt.key }))}
          value={selectedGoal}
          onChange={onGoalChange}
          placeholder={t("text.companion_onboarding_step5_goal_placeholder")}
          containerStyle={{ width: "100%" }}
          hideItemBorders
        />
      </OnboardingFormSection>
    </View>
  );
}

export default OnboardingStep5;
